import { dedent } from 'ts-dedent';
import { ColumnDataType, Provider, TokenAllowedApis, WorkflowCodeType, WorkflowComponentParamType, WorkflowComponentParameterMode, WorkflowParameterValueErrorType, Language, RoutingMatrixProviders, IsolinesProviders } from '../types';
import { isExpressionValue } from '../utils';
import { SINGLE_OUTPUT_NAME, SingleOutputWorkflowComponent } from './common/singleoutputcomponent';
const modes = {};
modes[RoutingMatrixProviders.TomTom] = ['Car', 'Truck', 'Pedestrian'];
modes[RoutingMatrixProviders.TravelTime] = [
    'Cycling',
    'Driving',
    'Driving+Train',
    'Driving+Public transport',
    'Public transport',
    'Walking',
    'Coach',
    'Bus',
    'Train',
    'Ferry',
    'Driving+Ferry',
    'Cycling+Ferry',
    'Cycling+Public transport'
];
const originsBatchSize = {};
originsBatchSize['default'] = {
    [RoutingMatrixProviders.TomTom]: 10,
    [RoutingMatrixProviders.TravelTime]: 1
};
originsBatchSize[Provider.Redshift] = {
    [RoutingMatrixProviders.TomTom]: 10,
    [RoutingMatrixProviders.TravelTime]: 1
};
const destinationsBatchSize = {};
destinationsBatchSize['default'] = {
    [RoutingMatrixProviders.TomTom]: 1000,
    [RoutingMatrixProviders.TravelTime]: 2000
};
destinationsBatchSize[Provider.Redshift] = {
    [RoutingMatrixProviders.TomTom]: 40,
    [RoutingMatrixProviders.TravelTime]: 400
};
const providerExtraOptions = {};
providerExtraOptions[IsolinesProviders.TomTom] = [
    {
        name: 'tomtom_departure_time',
        type: WorkflowComponentParamType.String,
        title: 'Departure time',
        optional: true,
        placeholder: 'YYYY-MM-DDThh:mm:ss'
    },
    {
        name: 'tomtom_traffic',
        type: WorkflowComponentParamType.Selection,
        title: 'Traffic',
        options: ['historical', 'live'],
        default: 'historical'
    },
    {
        name: 'tomtom_route_type',
        type: WorkflowComponentParamType.Selection,
        title: 'Route type',
        options: ['fastest', 'shortest'],
        default: 'fastest'
    },
    {
        name: 'tomtom_vehicle_max_speed',
        type: WorkflowComponentParamType.Number,
        title: 'Vehicle max speed (km/h)',
        default: 0,
        min: 0,
        max: 250,
        mode: WorkflowComponentParameterMode.Slider,
        allowExpressions: false,
        helper: 'workflows.parameterForm.tomtomVehicleMaxSpeedHelper'
    },
    {
        name: 'tomtom_avoid',
        type: WorkflowComponentParamType.Selection,
        title: 'Avoid',
        options: ['tollRoads', 'unpavedRoads'],
        mode: WorkflowComponentParameterMode.Multiple,
        allowExpressions: false,
        noDefault: true,
        optional: true
    }
];
providerExtraOptions[IsolinesProviders.TravelTime] = [
    {
        name: 'traveltime_departure_time',
        type: WorkflowComponentParamType.String,
        title: 'Departure time',
        optional: true,
        placeholder: 'YYYY-MM-DDThh:mm:ss'
    },
    {
        name: 'traveltime_travel_time',
        type: WorkflowComponentParamType.Number,
        title: 'Travel time (seconds)',
        default: 14400,
        min: 0,
        max: 14400,
        mode: WorkflowComponentParameterMode.Slider,
        allowExpressions: false
    }
];
class CreateRoutingMatrix extends SingleOutputWorkflowComponent {
    constructor() {
        super({
            name: 'native.createroutingmatrix',
            group: 'Spatial Constructors',
            title: 'Create Routing Matrix',
            description: `
        This component computes the routing matrix between a set of origins and destinations and return their distances and duration until arrival.
        `
        }, [
            {
                name: 'originstable',
                type: WorkflowComponentParamType.InputTable,
                title: 'Origins table'
            },
            {
                name: 'originsgeomcolumn',
                type: WorkflowComponentParamType.Column,
                title: 'Origins geometry column',
                parent: 'originstable',
                dataType: [ColumnDataType.Geography, ColumnDataType.Geometry]
            },
            {
                name: 'destinationstable',
                type: WorkflowComponentParamType.InputTable,
                title: 'Destinations table'
            },
            {
                name: 'destinationsgeomcolumn',
                type: WorkflowComponentParamType.Column,
                title: 'Destinations geometry column',
                parent: 'destinationstable',
                dataType: [ColumnDataType.Geography, ColumnDataType.Geometry]
            },
            {
                name: 'mode',
                type: WorkflowComponentParamType.Selection,
                title: 'Type of transport',
                options: ['Car', 'Truck']
            },
            {
                name: 'customoptions',
                type: WorkflowComponentParamType.String,
                title: 'Routing options',
                default: '',
                optional: true,
                mode: WorkflowComponentParameterMode.Multiline,
                allowExpressions: false,
                advanced: true
            }
        ]);
        this.supportedProviders = [
            Provider.BigQuery,
            Provider.Snowflake,
            Provider.Redshift,
            Provider.DatabricksRest
        ];
    }
    adaptComponent(provider, envVars) {
        if (envVars.mapProvider && envVars.mapProvider.routing) {
            if (modes[envVars.mapProvider.routing]) {
                const modeInput = this.inputs.find((input) => input.name === 'mode');
                if (modeInput) {
                    modeInput.options = modes[envVars.mapProvider.routing];
                }
            }
            if (providerExtraOptions[envVars.mapProvider.routing]) {
                this.inputs.push(...providerExtraOptions[envVars.mapProvider.routing]);
            }
            const defaultOriginsBatchSize = (originsBatchSize[provider] &&
                originsBatchSize[provider][envVars.mapProvider.routing]) ||
                originsBatchSize['default'][envVars.mapProvider.routing];
            const defaultDestinationsBatchSize = (destinationsBatchSize[provider] &&
                destinationsBatchSize[provider][envVars.mapProvider.routing]) ||
                destinationsBatchSize['default'][envVars.mapProvider.routing];
            const customOptionsInput = this.inputs.find((input) => input.name === 'customoptions');
            if (customOptionsInput) {
                customOptionsInput.default = JSON.stringify({
                    carto_origins_batch_size: defaultOriginsBatchSize,
                    carto_destinations_batch_size: defaultDestinationsBatchSize
                });
                customOptionsInput.placeholder = customOptionsInput.default;
            }
        }
    }
    async getCode({ workflow, params, connection, envVars, codeType, schemas }) {
        const storedProcedure = codeType === WorkflowCodeType.StoredProcedure;
        const tablename = connection.getOutputTableName(workflow.id, this.metadata.name, params, 'result', codeType);
        const temptablename = connection.getOutputTableName(workflow.id, this.metadata.name, params, 'temp', codeType);
        const preparedParams = this._prepareParameters(params);
        let query = '';
        const originsTable = connection.quoteName(preparedParams.originstable);
        const destinationsTable = connection.quoteName(preparedParams.destinationstable);
        let originsColString = '';
        const originsColumns = schemas?.input['originstable'];
        if (originsColumns) {
            originsColString = originsColumns
                .map((x) => `_origins.${x.name} AS origin_${x.name}`)
                .join(', ');
        }
        else {
            return {
                query: '',
                outputs: {}
            };
        }
        let destinationsColString = '';
        const destinationsColumns = schemas?.input['destinationstable'];
        if (destinationsColumns) {
            destinationsColString = destinationsColumns
                .map((x) => `_destinations.${x.name} AS destination_${x.name}`)
                .join(', ');
        }
        else {
            return {
                query: '',
                outputs: {}
            };
        }
        const select = dedent `
      SELECT ${connection.type === Provider.DatabricksRest
            ? '/*+ REPARTITION(40) */'
            : ''}
        ${originsColString},
        ${destinationsColString},
        CAST(NULL AS ${connection.paramTypeToDataProviderType(WorkflowComponentParamType.Number, '0')}) as route_distance,
        CAST(NULL AS ${connection.paramTypeToDataProviderType(WorkflowComponentParamType.Number, '0')}) as route_duration,
        CAST(NULL AS ${connection.paramTypeToDataProviderType(WorkflowComponentParamType.String, '')}) as carto_routing_matrix_metadata
      FROM
        ${originsTable} AS _origins,
        ${destinationsTable} AS _destinations
      `;
        let createTable = connection.getCreateTempTableQuery(connection.type === Provider.DatabricksRest ? temptablename : tablename, select, codeType, null);
        if (connection.type === Provider.DatabricksRest) {
            createTable = connection.wrapSQLCodeAsPythonCall(createTable);
        }
        const transportMode = preparedParams.mode
            .replace(' ', '_')
            .toLowerCase();
        const extraOptions = {};
        // TomTom extra options
        if (preparedParams.tomtom_departure_time != null) {
            extraOptions['departAt'] = preparedParams.tomtom_departure_time;
        }
        if (preparedParams.tomtom_traffic != null) {
            extraOptions['traffic'] = preparedParams.tomtom_traffic;
        }
        if (preparedParams.tomtom_route_type != null) {
            extraOptions['routeType'] = preparedParams.tomtom_route_type;
        }
        if (preparedParams.tomtom_vehicle_max_speed != null) {
            extraOptions['vehicleMaxSpeed'] = preparedParams.tomtom_vehicle_max_speed;
        }
        if (preparedParams.tomtom_avoid != null &&
            Array.isArray(preparedParams.tomtom_avoid) &&
            preparedParams.tomtom_avoid.length > 0) {
            extraOptions['avoid'] = preparedParams.tomtom_avoid;
        }
        // TravelTime extra options
        if (preparedParams.traveltime_departure_time != null) {
            extraOptions['departure_time'] = preparedParams.traveltime_departure_time;
        }
        if (preparedParams.traveltime_travel_time != null) {
            extraOptions['travel_time'] = preparedParams.traveltime_travel_time;
        }
        // Combine custom and extra options priorizing customoptions
        let options = preparedParams.customoptions
            ? {
                ...extraOptions,
                ...JSON.parse(preparedParams.customoptions)
            }
            : extraOptions;
        const nullOptions = connection.type === Provider.DatabricksRest ? "''" : 'NULL';
        options =
            Object.keys(options).length === 0
                ? nullOptions
                : `'${JSON.stringify(options)}'`;
        const routingMatrixTablename = connection.getOutputTableName(workflow.id, this.metadata.name, params, 'routingmatrix', codeType);
        const routingMatrixTablenameQuoted = connection.quoteName(routingMatrixTablename);
        switch (connection.type) {
            case Provider.BigQuery:
                query = dedent `
          ${storedProcedure
                    ? dedent `
              DECLARE tempOutputTable STRING DEFAULT '${connection.quoteName(connection.tempStoragePath)}.' || REPLACE(GENERATE_UUID(), '-', '_');
              BEGIN
              `
                    : ''}
        DROP TABLE IF EXISTS ${storedProcedure
                    ? 'tempOutputTable'
                    : `${connection.quoteName(routingMatrixTablename)}`};
        CALL ${connection.quoteName(envVars.analyticsToolboxDataset)}.CREATE_ROUTING_MATRIX(
          '${envVars.apiBaseUrl}',
          '${envVars.accessToken}',
          '${originsTable}',
          '${preparedParams.originsgeomcolumn}',
          '${destinationsTable}',
          '${preparedParams.destinationsgeomcolumn}',
          ${storedProcedure ? 'tempOutputTable' : `'${routingMatrixTablename}'`},
          '${transportMode}',
          ${options}
        );
        ${createTable}
        UPDATE ${connection.quoteName(tablename)} t
        SET t.route_distance = r.route_distance,
          t.route_duration = r.route_duration,
          t.carto_routing_matrix_metadata = r.carto_routing_matrix_metadata
        FROM ${storedProcedure
                    ? 'tempOutputTable'
                    : `${connection.quoteName(routingMatrixTablename)}`} r
        WHERE ST_ASTEXT(t.origin_${preparedParams.originsgeomcolumn}) = ST_ASTEXT(r.origin_geom)
          AND ST_ASTEXT(t.destination_${preparedParams.destinationsgeomcolumn}) = ST_ASTEXT(r.destination_geom);
        DROP TABLE IF EXISTS ${storedProcedure
                    ? 'tempOutputTable'
                    : `${connection.quoteName(routingMatrixTablename)}`};
        `;
                break;
            case Provider.Snowflake:
                query = dedent `
        ${storedProcedure
                    ? dedent `
              DECLARE
                tempOutputTable TEXT DEFAULT '${connection.quoteName(connection.tempStoragePath)}.outputtable' || REPLACE(UUID_STRING(), '-', '_');
              BEGIN
              `
                    : ''}
        DROP TABLE IF EXISTS ${storedProcedure
                    ? 'tempOutputTable'
                    : `${connection.quoteName(routingMatrixTablename)}`};
        CALL ${connection.quoteName(envVars.analyticsToolboxDataset)}.CREATE_ROUTING_MATRIX(
          '${originsTable}',
          '${preparedParams.originsgeomcolumn}',
          '${destinationsTable}',
          '${preparedParams.destinationsgeomcolumn}',
          ${storedProcedure ? 'tempOutputTable' : `'${routingMatrixTablename}'`},
          '${transportMode}',
          ${options},
          '${envVars.apiBaseUrl}',
          '${envVars.accessToken}'
        );
        ${createTable}
        UPDATE ${connection.quoteName(tablename)} t
        SET t.route_distance = r.route_distance,
          t.route_duration = r.route_duration,
          t.carto_routing_matrix_metadata = r.carto_routing_matrix_metadata
        FROM ${storedProcedure
                    ? 'tempOutputTable'
                    : `${connection.quoteName(routingMatrixTablename)}`} r
        WHERE ST_ASTEXT(t.origin_${preparedParams.originsgeomcolumn}) = ST_ASTEXT(r.origin_geom)
          AND ST_ASTEXT(t.destination_${preparedParams.destinationsgeomcolumn}) = ST_ASTEXT(r.destination_geom);
        DROP TABLE IF EXISTS ${storedProcedure
                    ? 'tempOutputTable'
                    : `${connection.quoteName(routingMatrixTablename)}`};
        `;
                break;
            case Provider.Redshift:
                query = dedent `
        ${storedProcedure
                    ? dedent `DECLARE
                  outputTableName VARCHAR(MAX) DEFAULT '${connection.quoteName(connection.tempStoragePath)}.outputtable' || REPLACE(EXTRACT(EPOCH FROM CURRENT_TIME)::VARCHAR, '.', '');
                  outputTableNameQuoted VARCHAR(MAX):= outputTableName;
                BEGIN
                `
                    : dedent `DECLARE
                outputTableName VARCHAR(MAX):= '${routingMatrixTablename}';
                outputTableNameQuoted VARCHAR(MAX):= '${routingMatrixTablenameQuoted}';
              BEGIN`}
        EXECUTE 'DROP TABLE IF EXISTS ' || outputTableNameQuoted;
        CALL ${connection.quoteName(envVars.analyticsToolboxDataset)}.CREATE_ROUTING_MATRIX(
          '${originsTable}',
          '${preparedParams.originsgeomcolumn}',
          '${destinationsTable}',
          '${preparedParams.destinationsgeomcolumn}',            
          outputTableName,
          '${transportMode}',
          ${options},
          '${envVars.apiBaseUrl}',
          '${envVars.accessToken}'
        );
        ${createTable}
        EXECUTE '
          UPDATE ${connection.quoteName(tablename)}
          SET route_distance = r.route_distance,
            route_duration = r.route_duration,
            carto_routing_matrix_metadata = r.carto_routing_matrix_metadata
          FROM ' || outputTableNameQuoted || ' r
          WHERE ST_ASTEXT(${connection.quoteName(tablename)}.origin_${preparedParams.originsgeomcolumn}) = ST_ASTEXT(r.origin_geom)
            AND ST_ASTEXT(${connection.quoteName(tablename)}.destination_${preparedParams.destinationsgeomcolumn}) = ST_ASTEXT(r.destination_geom)';
        EXECUTE 'DROP TABLE IF EXISTS ' || outputTableNameQuoted;
        END;
        `;
                break;
            case Provider.DatabricksRest:
                // Notice that in Databricks we are comparing geometries using binaries directly
                // instead of using ST_ASTEXT as the tables already contains WKB geometries.
                query = `
spark.sql('''DROP TABLE IF EXISTS ${connection.quoteName(routingMatrixTablename)}''')
spark.read.format("com.carto.analytics.toolbox.stored.procedures.ATStoredProcedureDatasource").option(
      "sql",
      """
      CALL_CARTO carto_un.carto.CREATE_ROUTING_MATRIX(
        '${envVars.apiBaseUrl}',
        '${envVars.accessToken}',
        '${originsTable}',
        '${preparedParams.originsgeomcolumn}',
        '${destinationsTable}',
        '${preparedParams.destinationsgeomcolumn}',
        '${connection.quoteName(routingMatrixTablename)}',
        '${transportMode}',
        ${options}
        );
      """
    ).load().collect();
${createTable}

spark.sql('''MERGE INTO ${connection.quoteName(temptablename)} AS t
USING ${connection.quoteName(routingMatrixTablename)} AS r
ON t.origin_${preparedParams.originsgeomcolumn} = r.origin_${preparedParams.originsgeomcolumn}
AND t.destination_${preparedParams.destinationsgeomcolumn} = r.destination_${preparedParams.destinationsgeomcolumn}
WHEN MATCHED THEN 
UPDATE SET 
    t.route_distance = r.route_distance,
    t.route_duration = r.route_duration,
    t.carto_routing_matrix_metadata = r.carto_routing_matrix_metadata;
''')
spark.sql('''DROP TABLE IF EXISTS ${connection.quoteName(routingMatrixTablename)}''')
${connection.wrapCodeInPrepareForVisualizationBlock(connection.quoteName(temptablename), connection.quoteName(tablename), `origin_${preparedParams.originsgeomcolumn}`, codeType)}
spark.sql('''DROP TABLE IF EXISTS ${connection.quoteName(routingMatrixTablename)}''')

        `;
                break;
            default:
                return null;
        }
        query = connection.wrapCodeInIfTableExistsBlock(query, [tablename], codeType);
        return {
            outputs: { result: tablename },
            query
        };
    }
    getRequiredGrants(connection) {
        return {
            grants: [],
            apis: [TokenAllowedApis.lds]
        };
    }
    requiredAnalyticsToolboxVersion(provider) {
        switch (provider) {
            case Provider.BigQuery:
                return '1.22.0';
            case Provider.Snowflake:
                return '1.20.0';
            case Provider.Redshift:
                return '1.11.0';
            default:
                return null;
        }
    }
    language(provider) {
        if (provider === Provider.DatabricksRest) {
            return Language.Python;
        }
        return Language.SQL;
    }
    getVersion(provider) {
        return '1';
    }
    checkInputValues(params) {
        const errors = super.checkInputValues(params);
        const { resolvedParamValues, schemas } = params;
        if (resolvedParamValues.customoptions &&
            !isExpressionValue(params.params.customoptions)) {
            try {
                const json = JSON.parse(resolvedParamValues.customoptions);
            }
            catch (e) {
                errors.push({
                    parameter: 'customoptions',
                    type: WorkflowParameterValueErrorType.WrongValue,
                    message: 'Invalid JSON string'
                });
            }
        }
        return errors;
    }
    canProvideOutputSchemas() {
        return true;
    }
    _getOutputSchemas({ workflow, params, schemas, connection, componentName }) {
        const tablenameOrigins = params.originstable;
        const tablenameDestinations = params.destinationstable;
        const columnsOrigins = this._getSchemaForTablename(tablenameOrigins, schemas, connection);
        const columnsDestinations = this._getSchemaForTablename(tablenameDestinations, schemas, connection);
        if (columnsOrigins == undefined || columnsDestinations == undefined) {
            return [];
        }
        const fqn = this._getDryRunOutputTableFqn(workflow, params, connection, componentName, SINGLE_OUTPUT_NAME);
        const outputSchema = columnsOrigins.map((column) => {
            return {
                name: `origin_${column.name}`,
                type: column.type
            };
        });
        columnsDestinations.forEach((column) => {
            outputSchema.push({
                name: `destination_${column.name}`,
                type: column.type
            });
        });
        outputSchema.push({
            name: 'route_distance',
            type: ColumnDataType.Number
        });
        outputSchema.push({
            name: 'route_duration',
            type: ColumnDataType.Number
        });
        outputSchema.push({
            name: 'carto_routing_matrix_metadata',
            type: ColumnDataType.String
        });
        if (connection.type === Provider.DatabricksRest) {
            outputSchema.push(...connection.BOUNDS_COLUMNS);
        }
        return [
            {
                fqn,
                schema: outputSchema
            }
        ];
    }
}
export { CreateRoutingMatrix };
